import { jsonrepair } from "jsonrepair";

export function parseJSONSafe(text: string): any {
  const trimmed = text.trim();

  // Try the text as-is first (handles both objects and arrays)
  try { return JSON.parse(trimmed); } catch { /* continue to extraction */ }

  // Array path: extract from first [ to last ]
  const af = trimmed.indexOf("[");
  const al = trimmed.lastIndexOf("]");
  if (af !== -1 && al !== -1 && al > af) {
    const slice = trimmed.slice(af, al + 1);
    try { return JSON.parse(slice); } catch { try { return JSON.parse(jsonrepair(slice)); } catch { /* fall through */ } }
  }

  // Object path: extract from first { to last }
  const of_ = trimmed.indexOf("{");
  const ol = trimmed.lastIndexOf("}");
  if (of_ === -1 || ol === -1) throw new Error("No valid JSON found in response.");
  try {
    return JSON.parse(trimmed.slice(of_, ol + 1));
  } catch {
    return JSON.parse(jsonrepair(trimmed.slice(of_)));
  }
}

export async function readAnthropicStream(res: Response, onChunk?: (accumulated: string) => void): Promise<string> {
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(`API error: ${res.status} — ${(errBody as any).error || (errBody as any).message || "unknown"}`);
  }
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let text = "";
  let messageStopReceived = false;
  let stopReason: string | null = null;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop()!;
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const payload = line.slice(6).trim();
      if (payload === "[DONE]") { messageStopReceived = true; continue; }
      try {
        const evt = JSON.parse(payload);
        if (evt.type === "error") {
          throw new Error(evt.error?.message || `Anthropic stream error: ${evt.error?.type || "unknown"}`);
        }
        if (evt.type === "message_delta" && evt.delta?.stop_reason) {
          stopReason = evt.delta.stop_reason;
        }
        if (evt.type === "message_stop") {
          messageStopReceived = true;
        }
        if (evt.type === "content_block_delta" && evt.delta?.type === "text_delta") {
          text += evt.delta.text;
          onChunk?.(text);
        }
      } catch (parseErr: any) {
        if (parseErr.message.startsWith("Anthropic stream error")) throw parseErr;
      }
    }
  }
  if (!messageStopReceived && text.length === 0) {
    throw new Error("El stream se cerró sin contenido — posible timeout o sobrecarga del modelo.");
  }
  if (stopReason === "max_tokens") {
    throw new Error("La respuesta excedió el límite de tokens para este tier. Intenta con una descripción de negocio más breve, o selecciona un tier superior (Mid-Size, Executive o Luxury) para mayor capacidad.");
  }
  if (!messageStopReceived && text.length > 0) {
    const trimmed = text.trim();

    // Check if the partial text parses or can be repaired (supports both {} and [])
    let looksComplete = false;
    try { JSON.parse(trimmed); looksComplete = true; } catch { /* try repair */ }

    if (!looksComplete) {
      // Last-chance recovery via jsonrepair — works for both objects and arrays.
      // Handles the 99%-complete case where a trailing brace/bracket was dropped
      // by a network hiccup or proxy-level idle timeout.
      try { JSON.parse(jsonrepair(trimmed)); return text; } catch { /* unrecoverable */ }
      throw new Error("La respuesta fue cortada antes de completarse — posible timeout de red. Intenta de nuevo.");
    }
  }
  return text;
}
